import { useCallback, useEffect, useRef, useState } from "react";
import { FormInstance } from "antd";
import { Meta, SetterConfig, UpdatePayload } from "../types";
import jsonLogic from "json-logic-js";
import { EVENT_TYPES } from "../constants";
// 添加路径解析工具函数
const getNestedValue = (obj: Record<string, any>, path: string): any => {
  if (!path || !obj) return undefined;
  
  // 如果路径不包含点号，直接返回属性值
  if (!path.includes('.')) {
    return obj[path];
  }
  
  // 分割路径并逐级访问
  const parts = path.split('.');
  let current = obj;
  
  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined;
    }
    current = current[part];
  }
  
  return current;
};

// 新增：将某个对象子树展开为“叶子路径 -> 值”的映射，只记录叶子（非对象）
const flattenLeaves = (obj: any, parentPath: string, out: Record<string, any>) => {
  const isObject =
    obj !== null &&
    typeof obj === 'object' &&
    !Array.isArray(obj);

  if (!isObject) {
    // 叶子或非对象，直接记录
    out[parentPath] = obj;
    return;
  }

  // 对象：递归其键
  Object.keys(obj).forEach((key) => {
    const nextPath = parentPath ? `${parentPath}.${key}` : key;
    const value = obj[key];
    const isChildObject =
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value);

    if (isChildObject) {
      flattenLeaves(value, nextPath, out);
    } else {
      out[nextPath] = value;
    }
  });
};

// 新增：扁平化变更对象为点路径
const flattenChangedValues = (obj: any, parentPath = ''): Array<[string, any]> => {
  const entries: Array<[string, any]> = [];
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      const nextPath = parentPath ? `${parentPath}.${key}` : key;
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        entries.push(...flattenChangedValues(value, nextPath));
      } else {
        entries.push([nextPath, value]);
      }
    });
  } else if (parentPath) {
    entries.push([parentPath, obj]);
  }
  return entries;
};
export const useFormBinding = (
  meta: Meta,
  form: FormInstance,
  handleEffects: (changedValues: Record<string, any>, allValues: Record<string, any>) => void,
  onChange: (payload: UpdatePayload) => void
) => {
  const isInitializedRef = useRef(false);
  const [visibleFields, setVisibleFields] = useState<Set<string>>(new Set());
  const prevMetaRef = useRef<Meta | null>(null);
  const prevFormValuesRef = useRef<Record<string, any>>({});

  // 1. 首先定义 hasMetaChanged
  const hasMetaChanged = useCallback((newMeta: Meta, oldMeta: Meta | null) => {
    if (!oldMeta) return true;
    
    const newPropsKeys = new Set(newMeta.setters.props.map(p => p.name));
    const oldPropsKeys = new Set(oldMeta.setters.props.map(p => p.name));
    
    if (newPropsKeys.size !== oldPropsKeys.size) return true;
    for (const key of newPropsKeys) {
      if (!oldPropsKeys.has(key)) return true;
    }
    
    const newDefaultValues = JSON.stringify(newMeta.defaultValues.props);
    const oldDefaultValues = JSON.stringify(oldMeta.defaultValues.props);
    if (newDefaultValues !== oldDefaultValues) return true;
    
    return false;
  }, []);

  // 2. 然后是 updateVisibility
  const updateVisibility = useCallback(
    (values: Record<string, any>) => {
      const newVisibleFields = new Set<string>();

      meta.setters.props.forEach((setter) => {
        const { name, visible } = setter;
        if (!visible || jsonLogic.apply(visible, values)) {
          newVisibleFields.add(name);
        }
      });

      setVisibleFields(newVisibleFields);
    },
    [meta.setters.props]
  );

  // 3. 其他工具函数
  const getSetterConfig = useCallback(
    (field: string): SetterConfig | undefined => {
      return meta.setters.props.find(
        (config) => config.name === field.split("/").pop()
      );
    },
    [meta.setters.props]
  );

  const getOptions = useCallback(
    (config: SetterConfig | undefined, values: Record<string, any>) => {
      if (!config?.options || Array.isArray(config.options)) {
        return config?.options;
      }

      const { mapping } = config.options;
      const dependencyValue = values[mapping.dependency];
      const sourceData = meta.datasource?.[mapping.source]?.[dependencyValue];

      if (!sourceData || !mapping.transform) {
        return sourceData;
      }

      return jsonLogic.apply(mapping.transform.map[1], {
        data: sourceData,
      });
    },
    [meta.datasource]
  );

  // 4. 值变更处理函数
  const handleValuesChange = useCallback(
    (changedValues: Record<string, any>, allValues: Record<string, any>) => {
      // 仅对 changedValues 的顶层键做子树对比，筛出真正变化的叶子路径
      Object.keys(changedValues).forEach((topKey) => {
        const prevSubtree = getNestedValue(prevFormValuesRef.current, topKey);
        const nextSubtree = getNestedValue(allValues, topKey);

        const prevMap: Record<string, any> = {};
        const nextMap: Record<string, any> = {};

        // 展开叶子映射（路径为完整点路径，如 resolveParams.param1）
        if (prevSubtree !== undefined) flattenLeaves(prevSubtree, topKey, prevMap);
        if (nextSubtree !== undefined) flattenLeaves(nextSubtree, topKey, nextMap);

        // 取并集路径，逐一比较，只有值真实变化才上报
        const allPaths = new Set<string>([...Object.keys(prevMap), ...Object.keys(nextMap)]);
        allPaths.forEach((path) => {
          const oldValue = prevMap[path];
          const newValue = nextMap[path];

          // 仅在值不同的时候上报（避免误报兄弟节点）
          const changed =
            // 区分 undefined 与 null
            (oldValue !== newValue) &&
            // 对于 NaN 需要特殊处理（可选）
            !(Number.isNaN(oldValue) && Number.isNaN(newValue));

          if (changed) {
            onChange?.({
              type: EVENT_TYPES.UPDATE_VALUE,
              data: allValues,
              path,
              oldValue,
              newValue,
            });
          }
        });
      });

      // 更新前一个值的缓存
      prevFormValuesRef.current = { ...allValues };
  
      // 更新字段可见性
      updateVisibility(allValues);
      // 处理联动效果（仍然传递原始 changedValues，兼容现有依赖）
      handleEffects(changedValues, allValues);
    },
    [handleEffects, onChange, updateVisibility]
  );

  // 5. 最后是 effect
  useEffect(() => {
    const metaChanged = hasMetaChanged(meta, prevMetaRef.current);
    
    if (!isInitializedRef.current || metaChanged) {
      form.setFieldsValue(meta.defaultValues.props);
      updateVisibility(meta.defaultValues.props);
      isInitializedRef.current = true;
      prevMetaRef.current = meta;
      prevFormValuesRef.current = meta.defaultValues.props;
    }
  }, [meta, form, updateVisibility, hasMetaChanged]);

  return {
    visibleFields,
    getSetterConfig,
    getOptions,
    handleValuesChange,
    updateVisibility,
  };
};
